import TAGS_CONFIG from "./tagsConfig";
import { getTopK } from "./getTopK";
import createPreferenceObject from "./preferences";
export {
    filterByType,
    filterByScore,
    defaultDerivedScore,
    getTopKTags,
    typeMapToCSV,
    transformToTypeMap,
    translateTypes,
    parsePreferenceTags,
    combineTags,
    scoreContentTags,
    getTopKContextTagTypes,
};

const THRESHOLDS = TAGS_CONFIG.thresholds;
const TAG_KEY_SET = {
    Bond: true,
    City: true,
    Classification: true,
    Commodity: true,
    Company: true,
    Country: true,
    Currency: true,
    Event: true,
    Fund: true,
    Index: true,
    Location: true,
    Organization: true,
    Person: true,
    Region: true,
    Topic: true,
};

const SPECIAL_MAPPING = {
    Person: "People",
    Company: "Tickers",
    Index: "Indexes",
    Topic: "Topics",
    Resourcetype: "Resourcetypes",
};

function filterByType(contentTags) {
    return contentTags.filter(({ type }) => Object.prototype.hasOwnProperty.call(TAG_KEY_SET, type));
}

function filterByScore(contentTags) {
    return contentTags.filter(({ type, derivedScore }) => derivedScore >= THRESHOLDS[type]);
}

function defaultDerivedScore(contentTags) {
    return contentTags.map((contentTag) => {
        let {
            derivedScore,
        } = contentTag;
        const {
            type,
        } = contentTag;

        if (derivedScore === undefined) {
            derivedScore = THRESHOLDS[type] || 0;
        }

        return Object.assign({}, contentTag, { derivedScore });
    });
}

function getTopKTags(array, k = TAGS_CONFIG.targetingParamLimit) {
    return getTopK(array, k);
}

function getTopKContextTagTypes(array, k = TAGS_CONFIG.contextTagTypesLimit) {
    return getTopK(array, k);
}

function typeMapToCSV(typeMap) {
    Object.keys(typeMap).forEach((key) => {
        typeMap[key] = typeMap[key].join(",");
    });

    return typeMap;
}

function transformToTypeMap(contentTags) {
    function typeMapReducer(typeMap, { id, type }) {
        typeMap[type] = typeMap[type] || [];
        typeMap[type].push(id);

        return typeMap;
    }

    return contentTags.reduce(typeMapReducer, {});
}

function translateTypes(typeMap) {
    return Object.keys(typeMap).reduce((translatedTypeMap, type) => {
        const translatedType = getTranslatedType(type);

        translatedTypeMap[translatedType] = typeMap[type];

        return translatedTypeMap;
    }, {});
}

function getTranslatedType(type) {
    return SPECIAL_MAPPING[type] || type;
}

function parsePreferenceTags(userInfo) {
    const preferenceTags = [];
    const {
        Tag: {
            Confidence = 0,
            Preferences = {},
        } = {},
    } = userInfo;

    if (Confidence < TAGS_CONFIG.confidenceThreshold) { // Only consider if confidence is greater than or equal to the threshold
        return preferenceTags;
    }

    Object.keys(TAG_KEY_SET).forEach((type) => {
        const tagPreferencesForType = Preferences[type];

        if (!Array.isArray(tagPreferencesForType)) {
            return;
        }

        tagPreferencesForType.forEach((tag) => {
            const preferredTag = createPreferenceObject(tag, Confidence);

            if (preferredTag) {
                preferenceTags.push(preferredTag);
            }
        });
    });

    return preferenceTags;
}

function combineTags({ contextTags, preferredTags }) {
    // create deep copy because we're modifying derivedScore within some tags
    const contextTagsCopy = JSON.parse(JSON.stringify(contextTags));
    const contextTagMap = {};

    // create lookup to map fullTagId to the existing contextTag
    contextTagsCopy.forEach((tag) => {
        const fullTagId = extractFullTagId(tag);

        contextTagMap[fullTagId] = tag;
    });

    const preferredOnly = [];

    preferredTags.forEach((preferredTag) => {
        const fullTagId = extractFullTagId(preferredTag);

        if (Object.prototype.hasOwnProperty.call(contextTagMap, fullTagId)) {
            const contextTag = contextTagMap[fullTagId];

            contextTag.derivedScore += preferredTag.derivedScore;
        } else {
            preferredOnly.push(preferredTag);
        }
    });

    return contextTagsCopy.concat(preferredOnly);
}

function extractFullTagId(tag) {
    const { type, id } = tag;

    return `${ type }|${ id }`;
}

function scoreContentTags(contentTags) {
    const scored = contentTags.reduce((acc, val) => {
        if (Object.prototype.hasOwnProperty.call(acc, val.type)) {
            acc[val.type] += val.derivedScore;
        } else {
            acc[val.type] = val.derivedScore;
        }

        return acc;
    }, {});

    return Object.keys(scored).map((prop) => {
        return {
            id: prop,
            type: "contextTagTypes",
            derivedScore: scored[prop],
        };
    });
}
